import { useEffect, useMemo } from "react";
import { Box, BoxProps, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import { useNavigate, useLocation } from "react-router-dom";
import qs from "qs";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppFallBackComponent } from "JS/React/Components/AppFallBackComponent";
import { config } from "JS/Config";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import {
    useReceivedMemberAudioGifts,
    useReceivedMemberVideoGifts,
} from "JS/React/Hooks/Gifts";
import {
    useCourses,
    useGlobalCourses,
    useReceivedCourses,
} from "JS/React/Hooks/Course/Course";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { GiftCategories } from "../Gifts";
import { AllTabs, ReceivedGift } from "JS/Models";
import { Content } from "JS/Models";
import { AudioCategoryTitle } from "../Audios";
import { useGlobalVideos, useVideos } from "JS/React/Hooks/Video";
import { VideoCategoryTitle } from "../Videos";

const StyledRoot = styled(Box)({});

export const AttachedSku = (props: AttachedSkuProps) => {
    const { className, ...rest } = props;

    const { attachmentType, mediaLinkId, memberId } = useQueryParams();

    const isSameUser = memberId === config.user.memberId;

    const { linkProvider } = useRouting();
    const { filteredMedia, receivedCourses, courses, loading } = useContent(
        isSameUser,
        attachmentType,
    );
    const { redirectToDesiredPage } = useRedirectToPage();

    const selectedMedia = useMemo(() => {
        if (attachmentType === "ReceivedGiftCourse")
            return receivedCourses.find((d) => d.sku_id === mediaLinkId);

        if (attachmentType === "Course")
            return courses.find((d) => d.sku_id === mediaLinkId);

        return filteredMedia.find((d) => d.sku_id === mediaLinkId);
    }, [filteredMedia, mediaLinkId, attachmentType, receivedCourses, courses]);

    useEffect(() => {
        if (isSameUser && selectedMedia) {
            redirectToDesiredPage(attachmentType, selectedMedia.nid);
        }
    }, [attachmentType, selectedMedia, isSameUser]);

    return (
        <Layout>
            <StyledRoot className={className} {...rest}>
                {!isSameUser && (
                    <AppFallBackComponent
                        desc={`Please login with IBO No ${memberId} to view this content.`}
                        routeText="Go To Homepage"
                        route={linkProvider.react.home()}
                    />
                )}

                {isSameUser && loading && (
                    <AppBackdropProgress
                        open={loading}
                        backdropText={"Redirecting"}
                    />
                )}

                {isSameUser && !loading && !selectedMedia && (
                    <AppFallBackComponent
                        desc={`Sku ${mediaLinkId} is unavailable`}
                        routeText="Go To Homepage"
                        route={linkProvider.react.home()}
                    />
                )}
            </StyledRoot>
        </Layout>
    );
};

const useRedirectToPage = () => {
    const navigate = useNavigate();
    const { linkProvider } = useRouting();

    const redirectToDesiredPage = (
        attachmentType: SkuAttachment,
        nid: string,
    ) => {
        if (attachmentType === "MP3") {
            navigate(
                linkProvider.react
                    .audios()
                    .detail(nid, AudioCategoryTitle.ALL_AUDIOS),
                { replace: true }
            );
        }

        if (attachmentType === "Video") {
            navigate(
                linkProvider.react
                    .videos()
                    .detail(nid, VideoCategoryTitle.ALL_VIDEOS, "true"),
                { replace: true }
            );
        }

        if (attachmentType === "ReceivedGiftMP3") {
            navigate(
                linkProvider.react
                    .gifts()
                    .giftAudioDetail(
                        nid,
                        GiftCategories.RECEIVED_AS_MEMBER,
                        AllTabs.AUDIOS,
                        "false",
                    ),
                { replace: true }
            );
        }

        if (attachmentType === "ReceivedGiftVideo") {
            navigate(
                linkProvider.react
                    .gifts()
                    .giftAudioDetail(
                        nid,
                        GiftCategories.RECEIVED_AS_MEMBER,
                        AllTabs.VIDEOS,
                        "true",
                    ),
                { replace: true }
            );
        }

        if (attachmentType === "ReceivedGiftCourse") {
            navigate(
                linkProvider.react.courses().detail(nid, { received: "true" }),
                { replace: true }
            );
        }

        if (attachmentType === "Course") {
            navigate(linkProvider.react.courses().detail(nid), { replace: true });
        }
    };

    return {
        redirectToDesiredPage,
    };
};

const useContent = (isSameUser: boolean, attachmentType: SkuAttachment) => {
    const skipReceivedMemberAudios = attachmentType !== "ReceivedGiftMP3";
    const skipReceivedMemberVideos = attachmentType !== "ReceivedGiftVideo";
    const skipReceivedMemberCourses = attachmentType !== "ReceivedGiftCourse";

    const { loading: audiosLoading, allAudios: audios } = useAudios();
    const { loading: videosLoading, allVideos: videos } = useVideos();
    const { courses } = useGlobalCourses();

    const { loading: receivedMemberAudioGiftsLoading, receivedMemberAudios } =
        useReceivedMemberAudioGifts(skipReceivedMemberAudios || !isSameUser);
    const { loading: receivedMemberVideoGiftsLoading, receivedMemberVideos } =
        useReceivedMemberVideoGifts(skipReceivedMemberVideos || !isSameUser);
    const { loading: coursesLoading } = useCourses();
    const { loading: receivedMemberCourseGiftsLoading, receivedCourses } =
        useReceivedCourses(skipReceivedMemberCourses || !isSameUser);

    const filteredMedia: (Content | ReceivedGift)[] = useMemo(() => {
        return [
            ...audios,
            ...videos,
            ...receivedMemberAudios,
            ...receivedMemberVideos,
        ];
    }, [videos, audios, receivedMemberAudios, receivedMemberVideos]);

    const loading =
        audiosLoading ||
        receivedMemberAudioGiftsLoading ||
        receivedMemberVideoGiftsLoading ||
        receivedMemberCourseGiftsLoading ||
        videosLoading ||
        coursesLoading;

    return {
        filteredMedia,
        receivedCourses,
        courses,
        loading,
    };
};

const useQueryParams = () => {
    const location = useLocation();

    const queryParams: AttachSkuQueryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const {
            attachment_type = "",
            media_link_id = "",
            member_id = "",
        } = parsedQueryString;

        return {
            attachment_type: attachment_type
                ? (attachment_type.toString() as SkuAttachment)
                : null,
            media_link_id: media_link_id ? media_link_id.toString() : null,
            member_id: member_id ? member_id.toString() : null,
        };
    }, [location?.search]);

    return {
        attachmentType: queryParams.attachment_type,
        mediaLinkId: queryParams.media_link_id,
        memberId: queryParams.member_id,
    };
};

export interface AttachedSkuProps extends InternalStandardProps<BoxProps> {}

type SkuAttachment =
    | "MP3"
    | "Video"
    | "ReceivedGiftMP3"
    | "ReceivedGiftVideo"
    | "ReceivedGiftCourse"
    | "Course";
interface AttachSkuQueryParams {
    attachment_type: SkuAttachment;
    media_link_id: string;
    member_id: string;
}
